import {useEffect, useState} from 'react'

type AnalysisTimeoutState = {
  apiURL: string
  headSha: string | undefined
  timeoutGeneration: number
  timedOut: boolean
}

export function useAnalysisPollingTimeout({
  apiURL,
  headSha,
  enabled,
  timeoutMs,
}: {
  apiURL: string
  headSha: string | undefined
  enabled: boolean
  timeoutMs: number
}): boolean {
  const [state, setState] = useState<AnalysisTimeoutState>({
    apiURL,
    headSha,
    timeoutGeneration: 0,
    timedOut: false,
  })

  // The endpoint and known analyzed head define one overall polling window.
  const identityChanged =
    state.apiURL !== apiURL || (state.headSha !== undefined && headSha !== undefined && state.headSha !== headSha)

  if (identityChanged) {
    // A new identity gets a fresh generation so callbacks from the previous window cannot expire it.
    setState({
      apiURL,
      headSha,
      timeoutGeneration: state.timeoutGeneration + 1,
      timedOut: false,
    })
  } else if (state.headSha === undefined && headSha !== undefined) {
    // Learning the initial head attaches it to the window already running for this endpoint.
    setState({...state, headSha})
  } else if (!enabled && state.timedOut) {
    // Clear an expired marker when polling is disabled so a later window can start cleanly.
    setState({...state, timedOut: false})
  }

  useEffect(() => {
    if (!enabled) return

    // Start one overall window while the hook is enabled; cleanup cancels it when that window ends.
    const timeoutGeneration = state.timeoutGeneration
    const timer = setTimeout(() => {
      // The captured generation prevents an old callback from expiring a newer identity.
      setState(current => (current.timeoutGeneration === timeoutGeneration ? {...current, timedOut: true} : current))
    }, timeoutMs)

    return () => clearTimeout(timer)
  }, [enabled, state.timeoutGeneration, timeoutMs])

  // Do not expose the previous identity's timeout during the render that resets it.
  return enabled && !identityChanged && state.timedOut
}
